/**
 * LegendPosterPage - 传奇作品海报生成页面
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import html2canvas from 'html2canvas';
import styles from './LegendPosterPage.module.css';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

const LegendPosterPage = () => {
    const { legendId } = useParams();
    const navigate = useNavigate();
    const { t, language, getLocaleCode } = useLanguage();
    const posterRef = useRef(null);

    const [legend, setLegend] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [posterGenerated, setPosterGenerated] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageDataUrl, setImageDataUrl] = useState(null);

    // 获取传奇作品详情
    useEffect(() => {
        const fetchLegend = async () => {
            try {
                const response = await fetch(`${API_BASE}/legends/${legendId}`);
                const data = await response.json();

                if (data.success) {
                    setLegend(data.data);
                } else {
                    setError(data.message || '加载失败');
                }
            } catch (err) {
                console.error('Fetch legend error:', err);
                setError('网络错误，请重试');
            } finally {
                setLoading(false);
            }
        };

        fetchLegend();
    }, [legendId]);

    // 预加载图片并转换为 base64 以解决 CORS 问题
    useEffect(() => {
        if (!legend?.cover_image_url) return;

        const loadImage = async () => {
            try {
                // 通过 fetch 获取图片数据
                const response = await fetch(legend.cover_image_url);
                const blob = await response.blob();

                // 转换为 data URL
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImageDataUrl(reader.result);
                    setImageLoaded(true);
                };
                reader.readAsDataURL(blob);
            } catch (err) {
                console.error('Failed to load image:', err);
                // 如果 fetch 失败，尝试直接使用原始 URL
                setImageDataUrl(legend.cover_image_url);
                setImageLoaded(true);
            }
        };

        loadImage();
    }, [legend?.cover_image_url]);

    // 生成海报图片
    const generatePoster = async () => {
        if (!posterRef.current) return;

        setGenerating(true);
        try {
            const canvas = await html2canvas(posterRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#000000'
            });

            // 转换为图片并下载
            const dataUrl = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.download = `legend-${legend?.serial_number || legendId}.png`;
            link.href = dataUrl;
            link.click();

            setPosterGenerated(true);

            // 记录分享
            try {
                await fetch(`${API_BASE}/legends/${legendId}/share/track`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ platform: 'poster' })
                });
            } catch (e) {
                console.log('Track share failed:', e);
            }
        } catch (err) {
            console.error('Generate poster error:', err);
            alert('生成海报失败，请重试');
        } finally {
            setGenerating(false);
        }
    };

    // 格式化价格
    const formatPrice = (price) => {
        if (!price) return '¥0';
        return `¥${Number(price).toLocaleString()}`;
    };

    // 格式化日期
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString(getLocaleCode(), {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>加载中...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.error}>
                    <p>{error}</p>
                    <button onClick={() => navigate(-1)}>返回</button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <button className={styles.backButton} onClick={() => navigate(-1)}>
                    ← 返回
                </button>
                <h1>生成分享海报</h1>
            </div>

            {/* 海报预览区域 */}
            <div className={styles.posterWrapper}>
                <div ref={posterRef} className={styles.poster}>
                    {/* 顶部品牌 */}
                    <div className={styles.posterHeader}>
                        <div className={styles.brandLogo}>HARLEY-DAVIDSON</div>
                        <div className={styles.brandSub}>FAT BOY CONFIGURATOR</div>
                    </div>

                    {/* 主图 */}
                    <div className={styles.posterImage}>
                        {imageLoaded && imageDataUrl ? (
                            <img
                                src={imageDataUrl}
                                alt={legend.title}
                            />
                        ) : legend?.cover_image_url ? (
                            <div className={styles.imageLoading}>图片加载中...</div>
                        ) : (
                            <div className={styles.noImage}>暂无图片</div>
                        )}
                    </div>

                    {/* 传奇标题 */}
                    <div className={styles.posterTitle}>
                        <h2>{legend?.title}</h2>
                        <div className={styles.serialNumber}>
                            #{String(legend?.serial_number || 0).padStart(7, '0')}
                        </div>
                    </div>

                    {/* 骑士信息 */}
                    <div className={styles.riderInfo}>
                        <div className={styles.riderAvatar}>
                            {legend?.rider?.avatar_url ? (
                                <img src={legend.rider.avatar_url} alt={legend.rider.nickname} />
                            ) : (
                                <span>{legend?.rider?.nickname?.charAt(0) || 'R'}</span>
                            )}
                        </div>
                        <div className={styles.riderDetails}>
                            <span className={styles.riderName}>
                                {legend?.rider?.nickname || '匿名骑士'}
                            </span>
                            <span className={styles.riderLevel}>
                                🔰 Lv.{legend?.rider?.level || 1} {legend?.rider?.title || '新手骑士'}
                            </span>
                        </div>
                    </div>

                    {/* 配置摘要 */}
                    {legend?.config_summary && (
                        <div className={styles.configSummary}>
                            <div className={styles.configPrice}>
                                {formatPrice(legend.config_summary.total_price)}
                            </div>
                        </div>
                    )}

                    {/* 统计数据 */}
                    <div className={styles.stats}>
                        <div className={styles.statItem}>
                            <span className={styles.statValue}>{legend?.salutes_count || 0}</span>
                            <span className={styles.statLabel}>致敬</span>
                        </div>
                        <div className={styles.statItem}>
                            <span className={styles.statValue}>{legend?.views_count || 0}</span>
                            <span className={styles.statLabel}>浏览</span>
                        </div>
                        <div className={styles.statItem}>
                            <span className={styles.statValue}>{legend?.shares_count || 0}</span>
                            <span className={styles.statLabel}>分享</span>
                        </div>
                    </div>

                    {/* 二维码区域 */}
                    <div className={styles.qrSection}>
                        <div className={styles.qrCode}>
                            <div className={styles.qrPlaceholder}>
                                扫码查看
                            </div>
                        </div>
                        <div className={styles.qrText}>
                            <span>扫码查看传奇详情</span>
                            <span className={styles.url}>fatboy.app/legend/{legendId?.slice(0, 8)}...</span>
                        </div>
                    </div>

                    {/* 底部 */}
                    <div className={styles.posterFooter}>
                        <div className={styles.forgeDate}>
                            铸造于 {formatDate(legend?.created_at)}
                        </div>
                        <div className={styles.watermark}>
                            铸造传奇 · FORGE YOUR LEGEND
                        </div>
                    </div>
                </div>
            </div>

            {/* 操作按钮 */}
            <div className={styles.actions}>
                <button
                    className={styles.generateButton}
                    onClick={generatePoster}
                    disabled={generating}
                >
                    {generating ? '生成中...' : posterGenerated ? '再次下载' : '下载海报'}
                </button>

                {posterGenerated && (
                    <div className={styles.successMessage}>
                        ✅ 海报已保存，快去分享吧！
                    </div>
                )}
            </div>
        </div>
    );
};

export default LegendPosterPage;
